import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

interface NotesDialogProps {
  open: boolean;
  notes: string;
  onClose: () => void;
  onSave: (notes: string) => void;
  title?: string;
}

const NotesDialog: React.FC<NotesDialogProps> = ({
  open,
  notes,
  onClose,
  onSave,
  title = 'Note',
}) => {
  const [notesText, setNotesText] = useState(notes);
  const muiTheme = useTheme();
  const { darkMode } = useAppTheme();

  // Update local state when prop changes
  useEffect(() => {
    setNotesText(notes);
  }, [notes]);

  const handleSave = () => {
    onSave(notesText);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      keepMounted
      scroll="paper"
      sx={{
        '& .MuiPaper-root': {
          backgroundColor: darkMode
            ? muiTheme.palette.background.paper
            : muiTheme.palette.background.paper,
          maxHeight: '80vh',
          maxWidth: '90vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <TextField
          autoFocus
          multiline
          minRows={6}
          maxRows={15}
          fullWidth
          margin="dense"
          variant="outlined"
          placeholder="Enter notes here..."
          value={notesText}
          onChange={e => setNotesText(e.target.value)}
          sx={{
            mt: 1,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: alpha(muiTheme.palette.primary.main, 0.3),
              },
              '&:hover fieldset': {
                borderColor: muiTheme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: muiTheme.palette.primary.main,
              },
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.background.paper, 0.7)
                : alpha(muiTheme.palette.common.white, 0.9),
              '& textarea': {
                resize: 'vertical',
              },
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: darkMode ? muiTheme.palette.primary.light : muiTheme.palette.primary.main,
          }}
        >
          CANCEL
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotesDialog;
